import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Link2, ShieldCheck } from 'lucide-react';
import { errMsg } from '../../../lib/aiInterviewApi';
import { absUrl, shareAPI } from '../../../lib/schedulingApi';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { copyText, fmtDate, Pill } from '../recruiter/common';

const INIT = { label: '', expires_days: 7, require_email_code: false, emails: '', include_recording: false };

const ShareRow = ({ s, onRevoke }) => (
  <div className="iv-status" style={{ alignItems: 'flex-start' }} data-testid={`share-row-${s.id}`}>
    <div style={{ minWidth: 0 }}>
      <div className="iv-row" style={{ gap: '0.35rem' }}>
        <b style={{ color: '#002A38' }}>{s.label || 'Shared link'}</b>
        <Pill tone={s.active ? 'green' : 'slate'} testId={`share-status-${s.id}`}>{s.revoked_at ? 'Revoked' : s.active ? 'Active' : 'Expired'}</Pill>
        {s.require_email_code && <Pill tone="sky">Email code</Pill>}
        {s.include_recording && <Pill tone="amber">Recording</Pill>}
      </div>
      <div className="iv-small" data-testid={`share-views-${s.id}`}>{s.views} view{s.views === 1 ? '' : 's'}{s.last_viewed_at ? ` · last ${fmtDate(s.last_viewed_at)}` : ''} · expires {fmtDate(s.expires_at)}</div>
      {s.require_email_code && <div className="iv-small">Allowed: {s.allowed_emails.join(', ')}{s.viewers.length ? ` · opened by ${s.viewers.join(', ')}` : ''}</div>}
    </div>
    {s.active && (
      <div className="iv-row" style={{ gap: '0.3rem' }}>
        <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => copyText(absUrl(s.path), toast)} data-testid={`share-copy-${s.id}`}><Copy size={13} /> Copy</button>
        <button type="button" className="iv-btn iv-btn-danger iv-btn-sm" onClick={() => onRevoke(s)} data-testid={`share-revoke-${s.id}`}>Revoke</button>
      </div>
    )}
  </div>
);

export default function ShareReportDialog({ open, onOpenChange, d }) {
  const [list, setList] = useState(null);
  const [f, setF] = useState(INIT);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const load = () => shareAPI.list(d.id).then(({ data }) => setList(data)).catch(() => setList([]));
  useEffect(() => { if (open) { setF(INIT); setCreated(null); load(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await shareAPI.create(d.id, { ...f, expires_days: Number(f.expires_days), allowed_emails: f.emails.split(/[\s,;]+/).filter(Boolean) });
      setCreated(data);
      copyText(absUrl(data.path), toast);
      load();
    } catch (e) {
      toast.error(errMsg(e, 'Could not create the link'));
    } finally {
      setBusy(false);
    }
  };
  const revoke = async (s) => {
    try {
      await shareAPI.revoke(d.id, s.id);
      toast.success('Link revoked');
      load();
    } catch (e) {
      toast.error(errMsg(e, 'Could not revoke'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="iv sc-dialog max-w-2xl" style={{ maxHeight: '92vh', overflowY: 'auto' }} data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle>Share a read-only report</DialogTitle>
          <DialogDescription>For hiring managers without a CHIRE login. The shared view hides contact details, ID and selfie photos, and raw integrity evidence.</DialogDescription>
        </DialogHeader>
        <div style={{ display: 'grid', gap: '0.7rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.6rem' }}>
            <input className="iv-input" placeholder="Label, e.g. For Priya (hiring manager)" value={f.label} onChange={(e) => set('label', e.target.value)} data-testid="share-label-input" />
            <select className="iv-select" value={f.expires_days} onChange={(e) => set('expires_days', e.target.value)} data-testid="share-expiry-select">
              {[1, 3, 7, 14, 30].map((n) => <option key={n} value={n}>{`Expires in ${n} day${n > 1 ? 's' : ''}`}</option>)}
            </select>
          </div>
          <label className="iv-check" style={{ borderTop: 0, padding: '0.3rem 0' }}>
            <input type="checkbox" checked={f.require_email_code} onChange={() => set('require_email_code', !f.require_email_code)} data-testid="share-gate-toggle" />
            <span><strong><ShieldCheck size={14} style={{ display: 'inline', verticalAlign: '-2px' }} /> Viewers must confirm their email</strong><span className="iv-small">Only the emails below can open it, after entering a one-time code.</span></span>
          </label>
          {f.require_email_code && <textarea className="iv-textarea" rows={2} placeholder="manager@company.com, lead@company.com" value={f.emails} onChange={(e) => set('emails', e.target.value)} data-testid="share-emails-input" />}
          <label className="iv-check" style={{ padding: '0.3rem 0' }}>
            <input type="checkbox" checked={f.include_recording} onChange={() => set('include_recording', !f.include_recording)} data-testid="share-recording-toggle" />
            <span><strong>Include the interview recording</strong><span className="iv-small">Off by default. The transcript is always included.</span></span>
          </label>
          <div className="iv-row-end" style={{ marginTop: 0 }}>
            <button type="button" className="iv-btn iv-btn-cta" disabled={busy || (f.require_email_code && !f.emails.trim())} onClick={create} data-testid="share-create-btn"><Link2 size={15} /> {busy ? 'Creating…' : 'Create & copy link'}</button>
          </div>
          {created && (
            <div className="iv-note is-good" data-testid="share-created">
              Link copied. <span className="sc-mono" data-testid="share-created-url">{absUrl(created.path)}</span>
            </div>
          )}
        </div>
        <div>
          <div className="iv-eyebrow" style={{ marginBottom: 6 }}>Links for this report</div>
          {list === null ? <p className="iv-small">Loading…</p> : !list.length ? <p className="iv-small" data-testid="share-list-empty">No links yet.</p> : (
            <div style={{ display: 'grid', gap: '0.5rem' }} data-testid="share-list">{list.map((s) => <ShareRow key={s.id} s={s} onRevoke={revoke} />)}</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
